// ** React Imports
import { useContext, useEffect, useMemo, useState } from "react";

// ** Redux & Store & Actions
import { useDispatch } from "react-redux";
import { mergeBulkUserInfoAndMedia } from "store/modules/modal";

// ** Third Party Components
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiperNew/react";
import { Virtual } from "swiperNew/modules";

// ** Custom Components
import Loader from "components/layout/Loader";
import ThumbnailMediaItemNew from "./ThumbnailMediaItemNew";
import NavigationArrowThumbnails from "./NavigationArrowThumbnails";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// Import Styles
import "swiperNew/css";
import { StyledLoadMoreItem } from "components/layout/Modal";

// #####################################################

const StyledThumbnailsSwiper = styled(Swiper)`
	display: flex;
	position: relative;
	height: 90px;
	width: 100%;
	box-sizing: border-box;
`;

// #####################################################

const ThumbnailsSwiperNew = ({
	carouselSwiper,
	thumbnailSwiper,
	setThumbnailSwiper,
	activeIndex,
	firstFilledSlideIndex,
	virtualSlides,
	thumbnailsPerView,
	spaceBetween,
	mediaMeta,
	parsedLink,
	showSwipers,
}) => {
	// ** Zmienne stanu komponentu
	const [centerSlides, setCenterSlides] = useState(false);
	const [showPrevArrow, setShowPrevArrow] = useState(false);
	const [showNextArrow, setShowNextArrow] = useState(false);
	const [isNavArrowClicked, setNavArrowClicked] = useState(false);
	const [isMoved, setMoved] = useState(false);
	const [isInitialized, setInitialized] = useState(false);

	const { isPhone } = useContext(deviceTypeContext);
	const dispatch = useDispatch();

	const { userLogin, mediaType, albumId } = parsedLink || {};
	const { right_amount, right_hash, left_hash } = mediaMeta || {};

	// ** Hook useMemo - Lista Slide'ów do wyrenderowania
	const slidesToRender = useMemo(() => {
		return virtualSlides.map((item, index) => {
			const isActive = activeIndex === index;
			return (
				<SwiperSlide
					className={isActive ? "active" : null}
					key={`thumbnail-slide-${item?.id}-${index}`}
					virtualIndex={index}
					onClick={() => {
						if (item._filled) {
							carouselSwiper.slideTo(index);
						}
					}}
				>
					<ThumbnailMediaItemNew item={item} isActive={isActive} />
				</SwiperSlide>
			);
		});
	}, [virtualSlides, activeIndex]);

	// ** Funkcja pobierajaca slajdy z prawej strony ('next')
	const handleGetNextThumbnails = () => {
		if (virtualSlides.length && right_amount > 0 && right_hash) {
			dispatch(
				mergeBulkUserInfoAndMedia({
					userLogin,
					elementId: virtualSlides[virtualSlides.length - 1].id,
					type: mediaType,
					vector: "right",
					album: albumId,
					rightHash: right_hash,
				})
			);
		}
	};

	// ** Funkcja pobierajaca slajdy z lewej strony ('prev')
	const handleGetPrevThumbnails = () => {
		if (
			virtualSlides[firstFilledSlideIndex] &&
			firstFilledSlideIndex !== 0 &&
			left_hash
		) {
			dispatch(
				mergeBulkUserInfoAndMedia({
					userLogin,
					elementId: virtualSlides[firstFilledSlideIndex]?.id,
					type: mediaType,
					vector: "left",
					album: albumId,
					leftHash: left_hash,
				})
			);
		}
	};

	// ** Efekt w zależności od liczby wyświetlanych slajdów oraz liczby slajdów w całym albumie
	// ** ustawia parametr 'centerSlides' na 'true' lub 'false' i tym samym włacza lub
	// ** wyłacza centrowanie slajdów w slajderze
	useEffect(() => {
		if (thumbnailSwiper?.visibleSlides?.length > 0) {
			if (thumbnailSwiper?.slidesGrid?.length > thumbnailsPerView) {
				setCenterSlides(true);
			} else {
				setCenterSlides(false);
			}
		}
	}, [
		thumbnailSwiper?.visibleSlides?.length,
		thumbnailSwiper?.slidesGrid?.length,
		thumbnailSwiper?.progress,
	]);

	// #####################################################

	return (
		<StyledThumbnailsSwiper
			className="noselect"
			style={{
				visibility: showSwipers ? "visible" : "hidden",
			}}
			modules={[Virtual]}
			slidesPerView={thumbnailsPerView}
			centeredSlides={centerSlides}
			centeredSlidesBounds
			virtual={{
				enabled: true,
				cache: true,
				addSlidesAfter: 0,
				addSlidesBefore: 0,
			}}
			watchSlidesProgress
			spaceBetween={spaceBetween}
			preventClicks
			preventClicksPropagation
			onAfterInit={() => {
				setTimeout(() => {
					setInitialized(true);
				}, 200);
			}}
			onSwiper={(swiper) => {
				setThumbnailSwiper(swiper);
			}}
			// ** W zależności od 'swiper.progress' ustawiamy widoczność
			// ** strzałek 'prev' i 'next'
			onSetTranslate={(swiper) => {
				if (
					swiper.progress > 0 &&
					virtualSlides.length > thumbnailsPerView
				) {
					//console.log("onSetTranslate - setShowPrevArrow(true)");
					setShowPrevArrow(true);
				} else {
					//console.log("onSetTranslate - setShowPrevArrow(false)");
					setShowPrevArrow(false);
				}
				if (
					swiper.progress < 1 &&
					virtualSlides.length > thumbnailsPerView
				) {
					//console.log("onSetTranslate - setShowNextArrow(true)");
					setShowNextArrow(true);
				} else {
					//console.log("onSetTranslate - setShowNextArrow(false)");
					setShowNextArrow(false);
				}
			}}
			// ** Po osiagnięciu ostatniego slajdu sprawdzamy czy sa jeszcze dostępne
			// ** kolejne slajdy do pobrania, jeżeli tak to pobieramy je i dodajemy do wirtualnej listy
			onReachEnd={() => {
				if (isInitialized) {
					handleGetNextThumbnails();
				}
			}}
			// ** W momencie przeciagania slidera w lewo sprawdzamy czy pozostały
			// ** jeszcze w tablicy slajdy 'nie wypełnione' (atrybut _filled === false),
			// ** natomiast w momencie przeciaganie slider w prawo sprawdzamy czy sa dostępne koljene slajdy.
			// ** Jeżeli tak pobieramy grafiki z API.
			onTransitionStart={(swiper) => {
				if (isInitialized && swiper.swipeDirection === "prev") {
					handleGetPrevThumbnails();
				}

				if (isInitialized && swiper.swipeDirection === "next") {
					handleGetNextThumbnails();
				}
			}}
			// cssMode
			// ** Po zakończeniu 'transition' sprawdzamy czy pozycja slidera zgadza się z pozycją
			// ** zapisaną w 'swiper.slidesGrid' jeżeli nie to przesuwamy slider do odpowiedniej pozycji,
			onTransitionEnd={(swiper) => {
				if (
					!isNavArrowClicked &&
					!isMoved &&
					Math.abs(swiper.getTranslate()) !==
						Math.abs(swiper.slidesGrid[activeIndex])
				) {
					swiper.translateTo(
						swiper.slidesGrid[activeIndex] * -1,
						300,
						false,
						true
					);
				}
			}}
			onSliderFirstMove={() => {
				setMoved(true);
			}}
		>
			{/* Strzałka nawigacji w lewo */}
			{!isPhone && showPrevArrow && (
				<NavigationArrowThumbnails
					direction="prev"
					swiper={thumbnailSwiper}
					thumbnailsPerView={thumbnailsPerView}
					getThumbnails={handleGetPrevThumbnails}
					setNavArrowClicked={setNavArrowClicked}
				/>
			)}

			{/* Slajdy do wyrenderowania */}
			{slidesToRender}

			{/* Loader */}
			{right_amount > 0 && (
				<SwiperSlide slot="container-end" key="loading-slide">
					<StyledLoadMoreItem type="button">
						<Loader color="white" size={20} />
					</StyledLoadMoreItem>
				</SwiperSlide>
			)}

			{/* Strzałka nawigacji w prawo */}
			{!isPhone && showNextArrow && (
				<NavigationArrowThumbnails
					direction="next"
					swiper={thumbnailSwiper}
					thumbnailsPerView={thumbnailsPerView}
					getThumbnails={handleGetNextThumbnails}
					setNavArrowClicked={setNavArrowClicked}
				/>
			)}
		</StyledThumbnailsSwiper>
	);
};

export default ThumbnailsSwiperNew;
